import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";
import usePayment from "hooks/usePayment";
import CustomPagination from "examples/CustomPagination/CustomPagination";
import paymentTableData from "layouts/payment/data/paymentTableData";
import MDButton from "components/MDButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { debounce } from "lodash";
import theme from "assets/theme";
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import useDashboard from "hooks/useDashboard";
function Tables() {
  const { getpaymentList, paymentList, paymentPreview, paymentSync } = usePayment();
  const { columns, rows } = paymentTableData();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const { searchTerm } = useDashboard();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPage, setTotalPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [distributor, setDistributor] = useState("");

  // Function to fetch dealers
  const fetchPayment = () => {
    const query = searchTerm.length >= 3 ? searchTerm : ""; // Apply search only if length >= 3
    getpaymentList({ limit, page, search: query });
  };

  // Debounced search function
  const debouncedfetchPayment = debounce(fetchPayment, 500);

  // Initial Load (Mandatory)
  useEffect(() => {
    fetchPayment(); // Always fetch initial data on mount
  }, []);

  // Search & Pagination Updates
  useEffect(() => {
    if (searchTerm.length === 0 || searchTerm.length >= 3) {
      debouncedfetchPayment();
    }
    return () => debouncedfetchPayment.cancel(); // Cleanup debounce
  }, [searchTerm, page, limit]);

  useEffect(() => {
    if (paymentList.page && paymentList.limit) {
      setPage(paymentList.page);
      setLimit(paymentList.limit);
      setTotalPage(paymentList.pages || 1);
      setTotal(paymentList.total || 0);
    }
  }, [paymentList]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    getpaymentList({ limit: 10, page: newPage });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setSelectedFile(null);
    setError("");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
    setError("");
  };

  const handleClosePreviewDialog = () => {
    setOpenPreviewDialog(false);
    setPreviewData([]);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    if (file) {
      const validMimeTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const validExtensions = [".xlsx", ".xls"];

      const fileExtension = file.name.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2);

      if (!validMimeTypes.includes(file.type) || !validExtensions.includes(`.${fileExtension}`)) {
        setError("Invalid file type. Please upload an Excel file (.xls, .xlsx).");
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError("");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".xls,.xlsx",
    multiple: false,
  });

  const handleConfirm = async () => {
    const payment = previewData
      .filter((item) => item.selected)
      .map(({ selected, id, ...rest }) => rest); // Remove 'selected' key

    const payload = { distributor, payment };

    try {
      const res = await paymentSync(payload);
      if (res.success) {
        alert("Payment synced successfully");
        handleClosePreviewDialog();
        fetchPayment();
      } else {
        alert(res.message || "Something went wrong!");
        setPreviewData(
          res?.error?.payment.map((item) => ({
            ...item,
            selected: item?.gstNoExist && item?.dealerExist && item?.territoryExist,
          }))
        );
        setDistributor(res?.error?.distributor);
      }
    } catch (error) {
      console.error("Payment sync error:", error);
      alert("An error occurred while syncing payment");
    }
  };

  const handleCheckboxChange = (index) => {
    const updatedData = [...previewData];
    updatedData[index].selected = !updatedData[index].selected;
    setPreviewData(updatedData);
  };

  const handleInputChange = (index, field, value) => {
    setPreviewData((prevData) => {
      return prevData.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
              ...(field === "dealer" && { dealerExist: true }),
              ...(field === "gstNo" && { gstNoExist: true }),
              ...(field === "territory" && { territoryExist: true }),
            }
          : item
      );
    });
  };

  const handleFileUpdate = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append("file", selectedFile);

      paymentPreview(formData)
        .then((response) => {
          if (response.success) {
            setDistributor(response.data.distributor);
            setPreviewData(
              response?.data?.preview?.map((item) => ({
                ...item,
                selected: true,
              }))
            );
            setOpenPreviewDialog(true);
          } else {
            window.alert(response.message || "Something went wrong!");
          }
          handleCloseDialog();
        })
        .catch((error) => {
          console.error("Error uploading file:", error);
          window.alert("Failed to upload and sync file. Please try again.");
        });

      setOpenDialog(false);
    } else {
      window.alert("Please select a file before updating.");
    }
  };

  const handleSelectAll = () => {
    const allSelected = previewData.every((item) => item.selected);
    const updatedData = previewData.map((item) => ({
      ...item,
      selected: !allSelected,
    }));
    setPreviewData(updatedData);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
              >
                <MDTypography variant="h6" color="white">
                  Payment Table
                </MDTypography>
                <MDButton variant="contained" onClick={handleOpenDialog}>
                  Update & Sync File
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
            <MDBox
              sx={{ width: "100%", display: "flex", justifyContent: "start", marginTop: "1rem" }}
            >
              <CustomPagination
                totalCount={totalPage}
                currentPage={page}
                onPageChange={handlePageChange}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      {/* Drag & Drop File Upload Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>File Update & Sync</DialogTitle>
        <DialogContent>
          <MDBox
            {...getRootProps()}
            sx={{
              border: "2px dashed #999",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragActive ? "#f0f0f0" : "#fff",
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 150, color: "#999" }} />
            <MDTypography sx={{ mt: 2 }}>
              {isDragActive
                ? "Drop the file here..."
                : "Drag & drop an Excel file here, or click to browse"}
            </MDTypography>
          </MDBox>
          {selectedFile && (
            <MDTypography sx={{ mt: 2, color: "green" }}>
              Selected file: {selectedFile.name}
            </MDTypography>
          )}
          {error && (
            <MDTypography color="error" sx={{ mt: 1 }}>
              {error}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleFileUpdate} color="info" disabled={!selectedFile}>
            Update & Sync
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPreviewDialog}
        onClose={handleClosePreviewDialog}
        fullScreen={fullScreen}
        aria-labelledby="responsive-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{distributor} - Payment Sync Preview</DialogTitle>
        <DialogContent>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: 500, overflowY: "auto", borderRadius: 2 }}
          >
            `{" "}
            <Table>
              <TableRow>
                <TableCell>
                  {" "}
                  <Checkbox
                    indeterminate={
                      previewData.some((item) => item.selected) &&
                      !previewData.every((item) => item.selected)
                    }
                    checked={previewData.every((item) => item.selected)}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Dealer</TableCell>
                <TableCell>GST No</TableCell>
                <TableCell>Out Standing</TableCell>
                <TableCell>Over Due</TableCell>
                <TableCell>Territory</TableCell>
              </TableRow>
              <TableBody>
                {previewData.map((row, index) => {
                  const isDealerExist = row.dealerExist === false;
                  const isGstNoExist = row.gstNoExist === false;
                  const territoryExist = row.territoryExist === false;
                  const isWrong = isDealerExist || isGstNoExist || territoryExist;
                  return (
                    <TableRow
                      key={index}
                      hover
                      sx={{ backgroundColor: isWrong ? "#ffebee" : "inherit" }} // Light red background for low payment
                    >
                      <TableCell>
                        <Checkbox
                          checked={row.selected}
                          onChange={() => handleCheckboxChange(index)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={row.dealer}
                          onChange={(e) => handleInputChange(index, "dealer", e.target.value)}
                          sx={{
                            border: isWrong && isDealerExist && "2px solid red",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={row.gstNo}
                          onChange={(e) => handleInputChange(index, "gstNo", e.target.value)}
                          sx={{
                            border: isWrong && isGstNoExist && "2px solid red",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={row.outstanding}
                          onChange={(e) => handleInputChange(index, "outstanding", e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={row.overdue}
                          onChange={(e) => handleInputChange(index, "overdue", e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={row.territory}
                          onChange={(e) => handleInputChange(index, "territory", e.target.value)}
                          sx={{
                            border: isWrong && territoryExist && "2px solid red",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            `
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClosePreviewDialog} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleConfirm} color="info">
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Tables;
